import React, { useEffect, useRef, createContext, useMemo, useCallback } from 'react';
import { Eventcalendar, setOptions, CalendarNav, CalendarPrev, CalendarNext, CalendarToday } from '@mobiscroll/react';

// Development flag - set to false to disable avatar deduplication

// Import extracted configurations
import {
    mobiscrollOptions,
    eventColors,
    zoomLevels,
    invalidResources,
    responsivePopup,
    createEventCalendarConfig,
    createEventPopupManagerConfig,
    createResourcesAndUtilsConfig,
    CreateMyScheduleEvent,
    // createMyScheduleEventContent,
    createShowMobileMenu,
    createOnDeleteClick,
    createHeaderText,
    createPopupButtons,
    createOnClose,
    handleSelectedDateChange,
    handleEventClick,
    handleEventCreated,
    handleEventDelete,
    handleEventDeleted,
    handleEventUpdated,
    handleEventHoverIn,
    handleEventHoverOut,
    handleMouseEnter,
    handleMouseLeave,
    createCursorPositionCalculators,
    createZoomLevelManager,
    createScrollToUser,
    setNorwegianHolidays as getNorwegianHolidays,
    createHolidayHandlers,
    createMyDayContent,
    flattenArr
} from './Calendar';
import {
    buildHolidayYmdSet,
    vacationWorkingDaySetsForYear,
    sickWorkingDaySetsForYear,
    sickChildWorkingDaySetsForYear,
    mapToCounts,
    mergeVacationCountsIntoResources
} from './Calendar/vacationWorkingDays';
import useCalendarData from '../Hooks/useFetchResources';
import { useMergedCalendarEvents } from '../Hooks/useMergedCalendarEvents';
import {
    applySummaryLineLanePositions,
    applyTimelineRowHeights,
} from './Calendar/summaryLineLayout';
// Import hook directly to avoid potential import issues
import { useEventPopup } from './Calendar/hooks/useEventPopup';
import { EventPopupManager } from './Calendar/EventPopupManager';

import '../styles.scss'
import "@mobiscroll/react/dist/css/mobiscroll.react.min.css";
import './CalendarComponent.css'

// ✅ LIVEBLOCKS: Import your Cursor, Avatar, and Liveblocks hooks
import Cursor, { CURSOR_COLORS } from "./Cursor"
import { Avatar } from './Avatar';
import { useOthers, useSelf, useUpdateMyPresence } from "@liveblocks/react";


// HOOKS
import { useAuth } from "../Hooks/AuthProvider";


import { Button } from '@mui/material';
import LoadingSpinner from './LoadingSpinner';


import isElectron from '../Utils/isElectron';

setOptions(mobiscrollOptions);

// CalendarContext for imperative commands
export const CalendarContext = createContext({
    goToToday: () => { },
    goToDate: (date) => { },
});

// ✅ LIVEBLOCKS: Helper to convert a hex color string to the object format our Cursor component expects
const getColorObjectFromHex = (hex) => {
    console.log("getColorObjectFromHex", hex);
    if (!hex) return CURSOR_COLORS.blue; // Fallback
    const matchingColor = Object.values(CURSOR_COLORS).find(c => c.default.toLowerCase() === hex.toLowerCase());
    return matchingColor || { default: hex, dim: hex }; // Return match or create a basic object
};




const Calendar = React.memo(() => {
    const REMOVE_DUPLICATE_AVATARS = false;
    const { user, userSettings, loading: authLoading } = useAuth();


    // ✅ LIVEBLOCKS: Convert the user's hex color to the object format
    const userColor = useMemo(() => {
        const colorSetting = userSettings?.["cursor-color"];

        // Case 1: The setting is an object from Firestore with 'color' and 'dim' properties.
        if (colorSetting && typeof colorSetting === 'object' && colorSetting.color && colorSetting.dim) {
            // Normalize the object to match what the Cursor component expects ({ default, dim }).
            return { default: colorSetting.color, dim: colorSetting.dim };
        }

        // Case 2: The setting is a string or is missing.
        const hexToConvert = typeof colorSetting === 'string' ? colorSetting : "#3498B7"; // Default hex
        return getColorObjectFromHex(hexToConvert);

    }, [userSettings]);
    const [mobileShow, setMobileShow] = React.useState(true);
    const [zoomLevel, setZoomLevel] = React.useState(1440);
    const [isHoverOpen, setIsHoverOpen] = React.useState(false);
    const timerRef = React.useRef(null);
    /** Defers clearing busy/vacation hover header flags until after Mobiscroll popup close animation */
    const hoverStripeClearTimerRef = React.useRef(null);
    const [eventContent, setEventContent] = React.useState([]);
    // const [calendarColors, setCalendarColors] = React.useState();
    const timelineRef = React.useRef(null);


    const [norwegianHolidays, setNorwegianHolidays] = React.useState([]);
    const [isHoverDayOpen, setIsHoverDayOpen] = React.useState(false);
    const [holidayName, setHolidayName] = React.useState(null);
    const [currentHolidayId, setCurrentHolidayId] = React.useState(null);
    const holidayAnchor = React.useRef(null);
    const holidayPopupRef = React.useRef(null);

    // Resource 11 popup state
    const [isResource11PopupOpen, setIsResource11PopupOpen] = React.useState(false);
    const [resource11Description, setResource11Description] = React.useState(null);
    const resource11Anchor = React.useRef(null);
    const resource11PopupRef = React.useRef(null);

    // Hover popup anchor ref to track the specific event element that triggered the hover
    const hoverEventAnchor = React.useRef(null);

    const [currentDate, setCurrentDate] = React.useState(new Date());

    useEffect(() => {
        // Check every minute if we've crossed midnight
        const interval = setInterval(() => {
            setCurrentDate((prevDate) => {
                const now = new Date();
                if (!prevDate || now.getDate() !== prevDate.getDate()) {
                    return now;
                }
                return prevDate;
            });
        }, 60000); // check every minute

        return () => clearInterval(interval);
    }, []); // Use functional update to avoid currentDate dependency



    // ✅ LIVEBLOCKS: Re-enable the hooks
    const others = useOthers();
    const self = useSelf();
    const updateMyPresence = useUpdateMyPresence();
    const [scrollPos, setScrollPos] = React.useState({ x: 0, y: 0, width: 0 })
    const [verticalScroll, setVerticalScroll] = React.useState(0)



    // Event state
    const [, setTempEvent] = React.useState(null);
    const [hoverIsBusy, setHoverIsBusy] = React.useState(false);
    const [hoverIsVacation, setHoverIsVacation] = React.useState(false);
    const originalResourcesRef = useRef(null); // Track original resources
    const tempEventRef = useRef(null); // Track temporary event for cleanup
    const [, startRef] = React.useState(null);
    const [, endRef] = React.useState(null);
    const [mySelectedDate, setSelectedDate] = React.useState(new Date());

    const {
        events: myEvents,
        setEvents: setMyEvents,
        eventsLoaded,
        setEventsLoaded,
        writeCollection: eventDatabase,
    } = useMergedCalendarEvents(mySelectedDate);

    const { resources, filteredEvents, summaryEvents, rowHeightsById, calendarColors } = useCalendarData(myEvents, currentDate);

    React.useEffect(() => {
        if (myEvents && myEvents.length > 0) {
            console.log('✅ Events loaded:', myEvents.length, 'events');
        }
    }, [myEvents]);

    React.useEffect(() => {
        if (filteredEvents && filteredEvents.length > 0) {
            console.log('✅ Filtered events ready:', filteredEvents.length, 'events');
        }
    }, [filteredEvents]);

    const isLoading = React.useMemo(() => {
        return authLoading || (!eventsLoaded && myEvents === undefined);
    }, [authLoading, eventsLoaded, myEvents]);

    // Use the popup hook for all popup-related state and logic
    const popupHook = useEventPopup({
        eventDatabase,
        myEvents,
        setMyEvents
        // Removed mySelectedDate dependency
    });

    // Destructure the popup hook for easier access
    const {
        // State
        isOpen,
        isEdit,
        anchor,
        popupEventTitle,
        popupEventDescription,
        popupEventAllDay,
        popupEventDate,
        popupEventTime,
        popupEventEdited,
        popupEventCreated,
        isChecked,
        isEditable,
        availability,
        popupIsRentman,
        isX,
        selectedColor,
        isManualSaving,

        // References
        tempEvent: popupTempEvent,

        // Actions
        setOpen,
        setAnchor,
        setTitle,
        setDescription,
        setAllDay,
        setTime,
        setCreated,
        setEdited,
        setSelectedColor,

        // Event handlers
        titleChange,
        descriptionChange,
        allDayChange,
        changeStartDate,
        changeEndDate,
        handleAvailabilityChange,
        handleEventX,
        handleEventInvoiced,
        handleResourceCheckInEvent,
        changeColor,

        // Main operations
        saveEvent,
        deleteLocalEvent,
        openEditPopup,
        openCreatePopup,

        // Helpers
        getDateFromTimestamp
    } = popupHook;

    const resourcesWithVacation = useMemo(() => {
        const selected =
            mySelectedDate instanceof Date &&
            typeof mySelectedDate.getTime === 'function' &&
            !Number.isNaN(mySelectedDate.getTime())
                ? mySelectedDate
                : new Date();
        const year = selected.getFullYear();
        const holidaySet = buildHolidayYmdSet(norwegianHolidays);
        const vacationDaySets = vacationWorkingDaySetsForYear(filteredEvents, year, holidaySet);
        const sickDaySets = sickWorkingDaySetsForYear(filteredEvents, year, holidaySet);
        const sickChildDaySets = sickChildWorkingDaySetsForYear(filteredEvents, year, holidaySet);
        const vacationCountsMap = mapToCounts(vacationDaySets);
        const sickCountsMap = mapToCounts(sickDaySets);
        const sickChildCountsMap = mapToCounts(sickChildDaySets);
        return mergeVacationCountsIntoResources(
            resources || [],
            vacationCountsMap,
            sickCountsMap,
            sickChildCountsMap
        );
    }, [filteredEvents, resources, mySelectedDate, norwegianHolidays]);

    useEffect(() => {
        if (!rowHeightsById?.size) return;

        const applySummaryLayout = () => {
            applyTimelineRowHeights(rowHeightsById);
            applySummaryLineLanePositions();
        };

        applySummaryLayout();
        const frame = requestAnimationFrame(applySummaryLayout);
        return () => cancelAnimationFrame(frame);
    }, [rowHeightsById, summaryEvents, resourcesWithVacation]);

    const myRenderResource = useCallback((resource) => {
        const n = resource.vacationWorkingDays;
        const s = resource.sickWorkingDays;
        const sc = resource.sickChildWorkingDays;
        const vacationLine =
            typeof n === 'number' && n > 0 ? (
                <div className="resource-vacation-working-days">Feriedager: {n}</div>
            ) : null;
        const sickLine =
            typeof s === 'number' && s > 0 ? (
                <div className="resource-sick-working-days">Sykedager: {s}</div>
            ) : null;
        const sickChildLine =
            typeof sc === 'number' && sc > 0 ? (
                <div className="resource-sick-child-working-days">Syke barn: {sc}</div>
            ) : null;

        if (resource.styling === 'header') {
            return (
                <div className="resource-header-content" data-resource-id={resource.id}>
                    <h6 className="resource-header-name">{resource.name}</h6>
                </div>
            );
        }
        if (resource.styling === 'person') {
            return (
                <div
                    className="resource-template-content resource-template-content--stacked"
                    data-resource-id={resource.id}
                >
                    <div className="resource-name resource-name-single-line">{resource.name}</div>
                    {vacationLine}
                    {sickLine}
                    {sickChildLine}
                    <div className="resource-description">{resource.description}</div>
                </div>
            );
        }
        return (
            <div
                className="resource-template-content resource-template-content--stacked"
                data-resource-id={resource.id}
            >
                <h5 className="resource-name resource-name-single-line">{resource.name}</h5>
                {vacationLine}
                {sickLine}
                {sickChildLine}
                <div className="resource-description">{resource.description}</div>
            </div>
        );
    }, []);

    // Calendar view settings


    // LiveBlocks multi-cursor functionality
    useEffect(() => {
        let cancelled = false;
        let cleanup = null;

        const attachScrollListener = () => {
            if (cancelled) return;
            const instance = timelineRef.current;
            if (!instance) {
                requestAnimationFrame(attachScrollListener);
                return;
            }

            // Wait for next render cycle to ensure elements are mounted
            setTimeout(() => {
                if (cancelled) return;
                const container = document.querySelector('.mbsc-timeline-grid-scroll');
                const containerWidth = document.querySelector('.mbsc-timeline-header-bg.mbsc-flex-1-0.mbsc-flex.mbsc-ios');
                const handleScroll = () => {
                    if (container && containerWidth) {
                        const containerWidthBox = containerWidth.getBoundingClientRect();
                        setScrollPos({
                            x: container.scrollLeft,
                            y: container.scrollTop,
                            width: containerWidthBox.width
                        });
                        // Track vertical scroll separately for cursor positioning
                        setVerticalScroll(container.scrollTop);
                    }
                };

                if (container) {
                    container.addEventListener('scroll', handleScroll);
                }

                cleanup = () => {
                    container?.removeEventListener('scroll', handleScroll);
                };
            }, 0);
        };

        attachScrollListener();
        return () => {
            cancelled = true;
            if (cleanup) cleanup();
        };
    }, []);

    // Invert scroll direction: vertical scroll -> horizontal scroll
    // Default to true (invert), but can be disabled via userSettings
    const invertScrollDirection = userSettings?.invertScrollDirection !== false; // Default true
    useEffect(() => {
        if (!invertScrollDirection) {
            return undefined;
        }

        let cancelled = false;
        let rafId = null;
        let timeoutId = null;
        let removeWheel = null;

        const clearWheel = () => {
            if (removeWheel) {
                removeWheel();
                removeWheel = null;
            }
        };

        const tryAttach = () => {
            if (cancelled) return;
            const calendarWrapper = document.querySelector('.motion-air-calendar-wrapper');
            const scrollContainer = document.querySelector('.mbsc-timeline-grid-scroll');

            if (!calendarWrapper || !scrollContainer) {
                rafId = requestAnimationFrame(tryAttach);
                return;
            }

            const handleWheel = (e) => {
                const shouldScrollHorizontally = invertScrollDirection ? !e.shiftKey : e.shiftKey;

                e.preventDefault();
                e.stopPropagation();

                if (shouldScrollHorizontally) {
                    const scrollAmount = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
                    scrollContainer.scrollLeft += scrollAmount;
                } else {
                    let scrollAmount = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : -e.deltaX;

                    if (invertScrollDirection) {
                        scrollAmount = -scrollAmount;
                    }

                    scrollContainer.scrollTop += scrollAmount;
                }
            };

            calendarWrapper.addEventListener('wheel', handleWheel, { passive: false });
            removeWheel = () => {
                calendarWrapper.removeEventListener('wheel', handleWheel);
            };
        };

        timeoutId = window.setTimeout(() => {
            timeoutId = null;
            tryAttach();
        }, 100);

        return () => {
            cancelled = true;
            if (rafId != null) {
                cancelAnimationFrame(rafId);
            }
            if (timeoutId != null) {
                clearTimeout(timeoutId);
            }
            clearWheel();
        };
    }, [invertScrollDirection]);




    // Store timeout reference for cleanup
    const cursorTimeoutRef = useRef(null);

    const mouseMove = (e) => {
        // ✅ LIVEBLOCKS: Re-enable cursor tracking logic
        if (!user) return; // Don't track if user is not loaded yet

        const rect = document.querySelector('.mbsc-timeline-header-bg.mbsc-flex-1-0.mbsc-flex.mbsc-ios')?.getBoundingClientRect();
        if (!rect) return; // Don't track if the calendar grid is not rendered

        // Calculate position relative to the calendar container with proper offsets
        let x = e.clientX - rect.left + 194; // x position within the scrollable div
        let y = e.clientY - rect.top + 98;  // y position within the scrollable div

        // Derive robust identity for Electron dev where Firebase user may lack email/displayName
        const fallbackEmail = (typeof window !== 'undefined') ? localStorage.getItem('electronEmail') : null;
        const safeEmail = user?.email || fallbackEmail || 'Unknown';
        const inferredNameFromEmail = (safeEmail && safeEmail.includes('@')) ? safeEmail.split('@')[0] : null;
        const safeDisplayName = user?.displayName || userSettings?.displayName || userSettings?.name || inferredNameFromEmail || 'Unknown';

        updateMyPresence({
            name: safeEmail,
            displayName: safeDisplayName,
            cursor: { x, y },
            scrollPosition: scrollPos,
            verticalScroll: verticalScroll,
            // Use profilePicture to match the Cursor component prop
            profilePicture: userSettings?.showProfilePictureInCursor ? userSettings.userImage : "",
            color: userColor, // Pass the color object
            lastActive: new Date()
        });

        // Clear existing timeout
        if (cursorTimeoutRef.current) {
            clearTimeout(cursorTimeoutRef.current);
        }

        // Set new timeout to hide cursor after 1 hour of inactivity
        cursorTimeoutRef.current = setTimeout(() => {
            updateMyPresence({ cursor: null });
        }, 60 * 60 * 1000); // 1 hour in milliseconds
    }




    // Set up the event handler once when component mounts (Electron only)
    useEffect(() => {
        if (isElectron()) {
            console.log('Setting up events handler for Electron');
            const handler = (events) => {
                try {
                    const normalizedEvents = events.map(event => {
                        const normalizeDate = (val) => {
                            if (!val) return null;
                            if (val instanceof Date) return val;
                            if (typeof val === 'string') return new Date(val);
                            if (typeof val.toDate === 'function') return val.toDate();
                            if (val._seconds) return new Date(val._seconds * 1000);
                            return val;
                        };

                        // Create a new event object with all possible date fields normalized
                        const normalizedEvent = {
                            ...event,
                            start: normalizeDate(event.start),
                            end: normalizeDate(event.end),
                            createdAt: normalizeDate(event.createdAt),
                            updatedAt: normalizeDate(event.updatedAt),
                            // Leave Rentman fields as they are - don't normalize them
                            // planStart, planEnd, usageStart, usageEnd remain as Firestore Timestamps
                        };

                        return normalizedEvent;
                    });
                    console.log('Normalized events:', normalizedEvents?.length || 0, 'events');
                    setMyEvents(normalizedEvents);
                    setEventsLoaded(true);
                } catch (error) {
                    console.error('Error processing events:', error);
                    setEventsLoaded(true); // Set loaded even on error to prevent infinite loading
                }
            };

            // Register IPC events handler, but only use it if we explicitly enable fallback
            const ipcFeedEnabledRef = { current: false };
            // Attach to window so we can toggle in sibling effects without re-registering
            try { window.__ipcFeedEnabledRef = ipcFeedEnabledRef; } catch (_) {}
            console.log('Registering events handler with electronAPI');
            window.electronAPI.onOpenEvent?.(({ id, start }) => {
                try {
                    const ts = start ? new Date(start) : null;
                    if (!ts || isNaN(ts.getTime())) return;
                    if (!(timelineRef.current && timelineRef.current.navigate)) return;
                    const target = new Date(ts.getTime() - 2 * 24 * 60 * 60 * 1000);
                    timelineRef.current.navigate(target);
                } catch (_) {}
            });

            window.electronAPI.onEventsUpdate((payload) => {
                if (!window.__ipcFeedEnabledRef?.current) return;
                handler(payload);
            });
            console.log('Events handler registered successfully');

            return () => {
                // Clean up when component unmounts
                try {
                    if (window.electronAPI.removeListener) {
                        window.electronAPI.removeListener('events-update', handler);
                    }
                } catch (error) {
                    console.warn('Error removing events listener:', error);
                }
            };
        }
    }, [setMyEvents, setEventsLoaded]); // Electron IPC fallback only

    // Ensure main-process scheduler subscribes to a rolling window (now -> +3 months)
    useEffect(() => {
        try {
            if (window.electronAPI && window.electronAPI.startEventsListener) {
                const start = new Date(Date.now() - 60 * 60 * 1000).toISOString(); // 1h back
                const end = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(); // +90 days
                window.electronAPI.startEventsListener(eventDatabase, start, end);
            }
        } catch (_) {}
        return () => {
            try { window.electronAPI && window.electronAPI.stopEventsListener && window.electronAPI.stopEventsListener(); } catch (_) {}
        };
    }, [eventDatabase]);


    // Webhook-based sync is now handled automatically on the backend
    // No need for manual frontend sync calls

    // // Create a Set of all valid resource IDs for fast lookup (including nested children)
    // const validResourceIds = useMemo(() => {
    //     const ids = new Set();

    //     const extractResourceIds = (resourceList) => {
    //         resourceList.forEach(resource => {
    //             if (resource && resource.id) {
    //                 ids.add(resource.id);
    //             }
    //             if (resource.children && Array.isArray(resource.children)) {
    //                 extractResourceIds(resource.children);
    //             }
    //         });
    //     };

    //     extractResourceIds(resources);
    //     return ids;
    // }, [resources]);

    // // Filter events to only include those with valid resource IDs
    // const filteredEvents = useMemo(() => {
    //     if (!myEvents || !Array.isArray(myEvents)) {
    //         return [];
    //     }
    // // ADD THIS DEBUG LOG:
    // console.log('🔍 DEBUG: All events before filtering:', myEvents.length);
    // console.log('🔍 DEBUG: Valid resource IDs:', Array.from(validResourceIds));

    // // Check if Rentman events are in the raw data
    // const rentmanEvents = myEvents.filter(e => e.rentmanEvent);
    // console.log('🔍 DEBUG: Rentman events found:', rentmanEvents.length);
    // if (rentmanEvents.length > 0) {
    //     // rentmanEvents.forEach(event => {
    //     //     console.log('🔍 DEBUG: Rentman event:', {
    //     //         ...event
    //     //     });
    //     // });

    //     // Check if any Rentman events are being filtered out
    //     const filteredRentmanEvents = rentmanEvents.filter(event => {
    //         if (event.visibleInCalendar === false) {
    //             console.log(`🔍 DEBUG: Rentman event "${event.title}" filtered out - visibleInCalendar is false`);
    //             return false;
    //         }

    //         if (!event.resource || !Array.isArray(event.resource)) {
    //             console.log(`🔍 DEBUG: Rentman event "${event.title}" filtered out - no valid resource array`);
    //             return false;
    //         }

    //         const hasInvalidResources = event.resource.some(resourceId => !validResourceIds.has(resourceId));
    //         if (hasInvalidResources) {
    //             console.log(`🔍 DEBUG: Rentman event "${event.title}" filtered out - invalid resources:`, 
    //                 event.resource.filter(id => !validResourceIds.has(id)));
    //             return false;
    //         }

    //         return true;
    //     });

    //     console.log(`🔍 DEBUG: Rentman events after filtering:`, filteredRentmanEvents.length);
    // }
    //     return myEvents.filter(event => {
    //         // Skip events that are explicitly hidden from calendar
    //         if (event.visibleInCalendar === false) {
    //             console.log(`🔍 DEBUG: Skipping event "${event.title}" - visibleInCalendar is false`);
    //             return false;
    //         }

    //         if (!event.resource || !Array.isArray(event.resource)) {
    //             return false; // Skip events without valid resource array
    //         }

    //         // Check if ALL resources in the event exist
    //         const hasInvalidResources = event.resource.some(resourceId => 
    //             !validResourceIds.has(resourceId)
    //         );

    //         if (hasInvalidResources) {
    //             console.warn(`Event "${event.title}" references missing resources:`, 
    //                 event.resource.filter(id => !validResourceIds.has(id))
    //             );
    //         }

    //         return !hasInvalidResources;
    //     });
    // }, [myEvents, validResourceIds]);

    // // Resource color management effect - optimized to prevent unnecessary rerenders
    // const calendarColorsMemo = useMemo(() => {
    //     if (!filteredEvents || !Array.isArray(filteredEvents)) {
    //         return [];
    //     }

    //     const freelanceResourceAvailable = () => {
    //         // Dynamically create resource arrays
    //         const crewResource = resources.find(resource => resource.name === "Crew");

    //         const freelanceResources = crewResource?.children?.find(child => child.name === "Freelancers")?.children?.map(child => child.id) || [];

    //         const equipmentResources = resources
    //             .find(resource => resource.name === "Equipment")?.children?.map(child => child.id) || [];

    //         const allResources = resources
    //             .flatMap(resource => resource.children || [])
    //             .map(child => child.id);

    //         // Initialize colors with predefined values
    //         const colors = [
    //             {
    //                 background: '#FFF3F4',
    //                 resource: Array.from(Array(101).keys()), // Background for Sundays
    //                 recurring: {
    //                     repeat: 'weekly',
    //                     weekDays: 'SU',
    //                 },
    //             },
    //             {
    //                 background: '#f7f2f2',
    //                 resource: Array.from(Array(101).keys()), // Background for Saturdays
    //                 recurring: {
    //                     repeat: 'weekly',
    //                     weekDays: 'SA',
    //                 },
    //             },
    //             {
    //                 date: currentDate, // Green background for "today" across all rows
    //                 background: 'rgb(227, 255, 227)',
    //                 resource: allResources,
    //             },
    //         ];

    //         const addColor = (startDate, endDate, resource, background, isEditable) => {
    //             const color = { start: startDate, end: endDate, resource, background };
    //             colors.push(color);

    //             if (!isEditable) {
    //                 const nonEditableColor = { ...color, background: '#808080' }; // Non-editable color
    //                 colors.push(nonEditableColor);
    //             }
    //         };

    //         if (filteredEvents && Array.isArray(filteredEvents)) {
    //             filteredEvents.forEach((event) => {
    //                 // Initialize dates based on allDay flag
    //                 let startDate, endDate;
    //                 if (event.allDay) {
    //                     startDate = event.start ? new Date(event.start) : null;
    //                     endDate = event.end ? new Date(event.end) : null;
    //                     // if (startDate) startDate.setHours(0, 0, 1);
    //                     // if (endDate) endDate.setHours(23, 59, 59);
    //                 } else {
    //                     startDate = event.start ? new Date(event.start) : null;
    //                     endDate = event.end ? new Date(event.end) : null;
    //                 }

    //                 // Check if startDate and endDate are valid
    //                 if (!startDate || isNaN(startDate.getTime()) || !endDate || isNaN(endDate.getTime())) {
    //                     console.warn('Invalid event dates:', event);
    //                     return; // Skip this event if dates are invalid
    //                 }

    //                 // Check for freelance resources
    //                 if (event.resource.some((r) => freelanceResources.includes(r))) {
    //                     const freelanceColorBackground = `${event.color}${Math.floor(0.4 * 255).toString(16).padStart(2, '0')}`;
    //                     addColor(startDate, endDate, [1001], freelanceColorBackground, event.editable);
    //                 }

    //                 // Check for equipment resources
    //                 if (event.resource.some((r) => equipmentResources.includes(r))) {
    //                     const equipmentColorBackground = `${event.color}${Math.floor(0.4 * 255).toString(16).padStart(2, '0')}`;
    //                     addColor(startDate, endDate, [101], equipmentColorBackground, event.editable);
    //                 }
    //             });
    //         }

    //         return colors;
    //     };

    //     return freelanceResourceAvailable();
    // }, [filteredEvents, resources]); // Removed currentDate dependency - only recalculate when events/resources change

    // Update calendar colors only when the memoized value changes
    // useEffect(() => {
    //     setCalendarColors(calendarColorsMemo);
    // }, [calendarColorsMemo]);


    // Removed duplicate handleResourceCheckInEvent - now provided by useEventPopup hook

    const myScheduleEvent = CreateMyScheduleEvent(mobileShow);

    // const myScheduleEventContent = createMyScheduleEventContent();


    // const baseResources = useFirestoreResources();

    // // 3. GENERATE the final, combined list of resources.
    // // This hook takes the base resources and intelligently merges them with
    // // the dynamic project/subproject rows generated from your live event data.
    // const allResources = useDynamicResources(baseResources, filteredEvents);


    // Removed unused renderCustomResource function

    // Removed unused renderResourceHeader function


    // Calendar configuration

    // Event handlers and utilities
    const showMobileMenu = createShowMobileMenu(mobileShow, setMobileShow);

    // Track last processed date to prevent duplicate onSelectedDateChange events
    const lastProcessedDateRef = useRef(null);

    // Use the popup system's tempEvent ref and deleteLocalEvent for deletion
    const onDeleteClick = createOnDeleteClick(deleteLocalEvent, popupTempEvent);

    // scheduler options
    const onSelectedDateChange = React.useCallback((event) => {
        // Prevent duplicate date change events from Mobiscroll navigation
        const newDate = event.date;
        const lastDate = lastProcessedDateRef.current;

        // Debug: Log the raw event data
        console.log('Mobiscroll onSelectedDateChange event:', {
            event: event,
            newDate: newDate,
            newDateType: typeof newDate,
            newDateConstructor: newDate?.constructor?.name,
            newDateIsValid: newDate instanceof Date && !isNaN(newDate.getTime()),
            lastDate: lastDate,
            lastDateType: typeof lastDate
        });

        // Only process if the date actually changed (not just a reference change)
        if (!lastDate || newDate.getTime() !== lastDate.getTime()) {
            console.log('Processing date change from:', lastDate?.toISOString(), 'to:', newDate.toISOString());
            lastProcessedDateRef.current = newDate;
            handleSelectedDateChange(event, setSelectedDate);
        } else {
            console.log('Skipping duplicate date change event for:', newDate.toISOString());
        }
    }, [setSelectedDate]);

    // console.log('############    myEvents', myEvents);

    const onEventClick = React.useCallback((args) => {
        handleEventClick(args, originalResourcesRef, openEditPopup);
    }, [openEditPopup]);

    const onEventCreated = React.useCallback((args) => {
        handleEventCreated(args, originalResourcesRef, openCreatePopup, popupEventAllDay, isManualSaving, eventColors, tempEventRef);
    }, [openCreatePopup, popupEventAllDay, isManualSaving]);

    const onEventDelete = React.useCallback((args) => {
        handleEventDelete(args, popupHook.deleteLocalEvent);
    }, [popupHook.deleteLocalEvent]);

    const onEventDeleted = React.useCallback((args) => {
        handleEventDeleted(args, popupHook.deleteLocalEvent);
    }, [popupHook.deleteLocalEvent]);

    const onEventUpdated = React.useCallback((args) => {
        handleEventUpdated(args, eventDatabase);
    }, [eventDatabase]); // Removed mySelectedDate dependency

    // Popup configuration
    const headerText = React.useMemo(() => createHeaderText(isEdit), [isEdit]);
    const popupButtons = React.useMemo(() => createPopupButtons(isEdit, saveEvent), [isEdit, saveEvent]);

    const onClose = createOnClose(setOpen, tempEventRef);

    const clearHoverStripes = React.useCallback(() => {
        setHoverIsBusy(false);
        setHoverIsVacation(false);
    }, []);

    const cancelHoverStripeClear = React.useCallback(() => {
        if (hoverStripeClearTimerRef.current) {
            clearTimeout(hoverStripeClearTimerRef.current);
            hoverStripeClearTimerRef.current = null;
        }
    }, []);

    const scheduleHoverStripeClear = React.useCallback(() => {
        cancelHoverStripeClear();
        hoverStripeClearTimerRef.current = setTimeout(() => {
            hoverStripeClearTimerRef.current = null;
            clearHoverStripes();
        }, 280);
    }, [cancelHoverStripeClear, clearHoverStripes]);

    React.useEffect(() => () => cancelHoverStripeClear(), [cancelHoverStripeClear]);

    // Event handlers


    const onEventHoverIn = React.useCallback((args) => {
        cancelHoverStripeClear();
        handleEventHoverIn(
            args,
            setTitle,
            setAllDay,
            setDescription,
            setAnchor,
            setSelectedColor,
            setIsHoverOpen,
            setTempEvent,
            setTime,
            setCreated,
            setEdited,
            setEventContent,
            resources,
            flattenArr,
            // Resource 11 popup parameters
            setIsResource11PopupOpen,
            setResource11Description,
            resource11Anchor,
            // Hover event anchor for mouse movement tracking
            hoverEventAnchor,
            setHoverIsBusy,
            setHoverIsVacation
        );
    }, [cancelHoverStripeClear, setTitle, setAllDay, setDescription, setAnchor, setSelectedColor, setIsHoverOpen, setTempEvent, setTime, setCreated, setEdited, setEventContent, resources, setIsResource11PopupOpen, setResource11Description, resource11Anchor, hoverEventAnchor, setHoverIsBusy, setHoverIsVacation]);

    const onEventHoverOut = React.useCallback(() => {
        handleEventHoverOut(timerRef, setIsHoverOpen, scheduleHoverStripeClear);
    }, [timerRef, setIsHoverOpen, scheduleHoverStripeClear]);

    const onMouseEnter = React.useCallback(() => {
        handleMouseEnter(timerRef);
    }, [timerRef]);

    const onMouseLeave = React.useCallback(() => {
        handleMouseLeave(timerRef, setIsHoverOpen, scheduleHoverStripeClear);
    }, [timerRef, setIsHoverOpen, scheduleHoverStripeClear]);

    // Force close hover popup
    const forceCloseHoverPopup = React.useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setIsHoverOpen(false);
        scheduleHoverStripeClear();
        // Clear the hover event anchor reference
        if (hoverEventAnchor.current) {
            hoverEventAnchor.current = null;
        }
    }, [timerRef, setIsHoverOpen, hoverEventAnchor, scheduleHoverStripeClear]);

    // Resource 11 popup handlers
    const onResource11MouseEnter = React.useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    }, [timerRef]);

    const onResource11MouseLeave = React.useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            setIsResource11PopupOpen(false);
        }, 150);
    }, [timerRef, setIsResource11PopupOpen]);

    const forceCloseResource11Popup = React.useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setIsResource11PopupOpen(false);
    }, [timerRef, setIsResource11PopupOpen]);



    const { cursorPosCalcX, cursorPosCalcY } = createCursorPositionCalculators(mobileShow, scrollPos);

    const updatePresence = () => {
        // ✅ LIVEBLOCKS: Update lastActive when leaving the window but keep cursor position
        // The cursor will stay at the last position and turn grey after 1 minute
        // It will be set to null after 1 hour by the existing timeout
        updateMyPresence({ 
            lastActive: new Date().toISOString()
        });
    }


    createZoomLevelManager(zoomLevel, setZoomLevel, zoomLevels);


    const scrollToUser = createScrollToUser(others, user); // others is empty array, so this will be no-op

    // Create holiday handlers first
    const { onHolidayMouseEnter, onHolidayMouseLeave, forceClosePopup, setupScrollListeners } = createHolidayHandlers(timerRef, setIsHoverDayOpen, setHolidayName, setCurrentHolidayId);

    // Memoize holiday data to prevent unnecessary recalculations
    const holidayData = useMemo(() => {
        try {
            // Use current year for holidays
            const currentYear = new Date().getFullYear();
            const targetDate = new Date(currentYear, 0, 1);
            return getNorwegianHolidays(targetDate);
        } catch (error) {
            console.error('CalendarComponent: Error loading holidays:', error);
            return [];
        }
    }, []); // Only calculate once

    // Update holidays when component mounts
    useEffect(() => {
        setNorwegianHolidays(holidayData || []);
    }, [holidayData]);

    // Recompute holidays when the viewed year changes
    const lastHolidayYearRef = useRef(null);
    useEffect(() => {
        try {
            const selected = (mySelectedDate instanceof Date && typeof mySelectedDate.getTime === 'function' && !isNaN(mySelectedDate.getTime()))
                ? mySelectedDate
                : new Date();
            const year = selected.getFullYear();

            if (lastHolidayYearRef.current === year) {
                return;
            }
            lastHolidayYearRef.current = year;

            const targetDate = new Date(year, 0, 1);
            const data = getNorwegianHolidays(targetDate) || [];
            setNorwegianHolidays(data);

            // Reset any open holiday popup state to avoid stale anchors
            setIsHoverDayOpen(false);
            setHolidayName(null);
            setCurrentHolidayId(null);
            if (holidayAnchor && holidayAnchor.current) {
                holidayAnchor.current = null;
            }
        } catch (error) {
            console.error('CalendarComponent: Error updating holidays for year change:', error);
            setNorwegianHolidays([]);
        }
    }, [mySelectedDate]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            if (cursorTimeoutRef.current) {
                clearTimeout(cursorTimeoutRef.current);
            }
        };
    }, []);

    // Setup scroll listeners for holiday popup
    useEffect(() => {
        const cleanupScrollListeners = setupScrollListeners();

        return cleanupScrollListeners;
    }, [setupScrollListeners]);

    // Position holiday popup when it's shown
    useEffect(() => {
        if (isHoverDayOpen && holidayAnchor.current && holidayPopupRef.current && currentHolidayId) {
            // Get fresh references to ensure we're using current data
            const currentAnchor = holidayAnchor.current;
            const currentPopup = holidayPopupRef.current;

            // Immediately hide the popup to prevent text changes in old location
            currentPopup.style.opacity = '0';

            const anchorRect = currentAnchor.getBoundingClientRect();
            const calendarContainer = document.querySelector('.motion-air-calendar-wrapper');

            if (calendarContainer) {
                const containerRect = calendarContainer.getBoundingClientRect();

                // Get the current horizontal scroll position
                const scrollContainer = document.querySelector('.mbsc-timeline-grid-scroll');
                const horizontalScroll = scrollContainer ? scrollContainer.scrollLeft : 0;

                // Position the popup while invisible, accounting for horizontal scroll
                positionAndShowPopup(currentPopup, anchorRect, containerRect, horizontalScroll);
            }
        }
    }, [isHoverDayOpen, currentHolidayId]); // Use currentHolidayId for reliable tracking

    // Position resource 11 popup when it's shown
    useEffect(() => {
        if (isResource11PopupOpen && resource11Anchor.current && resource11PopupRef.current && resource11Description) {
            // Get fresh references to ensure we're using current data
            const currentAnchor = resource11Anchor.current;
            const currentPopup = resource11PopupRef.current;

            // Immediately hide the popup to prevent text changes in old location
            currentPopup.style.opacity = '0';

            // Get mouse position from the anchor
            const mouseX = currentAnchor.mouseX - 100 || 0;
            const mouseY = currentAnchor.mouseY || 0;

            // Position the popup at mouse cursor with offset
            const offsetX = 10; // 10px to the right of cursor
            const offsetY = 10; // 10px below cursor

            // Set position relative to viewport
            currentPopup.style.left = `${mouseX + offsetX}px`;
            currentPopup.style.top = `${mouseY + offsetY}px`;

            // Small delay before fading in to ensure smooth transition
            setTimeout(() => {
                currentPopup.style.opacity = '1';
            }, 50); // 50ms delay for smooth appearance
        }
    }, [isResource11PopupOpen, resource11Description]); // Use resource11Description for reliable tracking

    // Helper function to position and show the popup
    const positionAndShowPopup = (popup, anchorRect, containerRect) => {
        // Get the horizontal scroll offset from the calendar container
        const calendarContainer = document.querySelector('.motion-air-calendar-wrapper');
        const horizontalScrollOffset = calendarContainer ? calendarContainer.scrollLeft || 0 : 0;

        // Calculate position relative to the calendar container, accounting for horizontal scroll
        const relativeLeft = anchorRect.left - containerRect.left + horizontalScrollOffset;
        const relativeTop = anchorRect.bottom - containerRect.top + 2; // 2px below holiday

        // Center the popup horizontally over the holiday element
        // First position the popup to get its actual width, then center it
        popup.style.left = `${relativeLeft}px`;
        popup.style.top = `${relativeTop}px`;

        // Now get the actual popup width and center it
        const popupWidth = popup.offsetWidth;
        const centeredLeft = relativeLeft + (anchorRect.width / 2) - (popupWidth / 2);

        // Apply the centered position
        popup.style.left = `${centeredLeft}px`;

        // Small delay before fading in to ensure smooth transition
        setTimeout(() => {
            popup.style.opacity = '1';
        }, 50); // 50ms delay for smooth appearance
    };


    // Global mouse listener to close popup when moving away
    useEffect(() => {
        if (isHoverDayOpen) {
            const handleGlobalMouseMove = (e) => {
                if (holidayAnchor.current && holidayPopupRef.current) {
                    const anchorRect = holidayAnchor.current.getBoundingClientRect();
                    const popupRect = holidayPopupRef.current.getBoundingClientRect();

                    // Check if mouse is outside both the holiday element and popup
                    const isOutsideHoliday = !(
                        e.clientX >= anchorRect.left &&
                        e.clientX <= anchorRect.right &&
                        e.clientY >= anchorRect.top &&
                        e.clientY <= anchorRect.bottom
                    );

                    const isOutsidePopup = !(
                        e.clientX >= popupRect.left &&
                        e.clientX <= popupRect.right &&
                        e.clientY >= popupRect.top &&
                        e.clientY <= popupRect.bottom
                    );

                    if (isOutsideHoliday && isOutsidePopup) {
                        forceClosePopup();
                    }
                }
            };

            document.addEventListener('mousemove', handleGlobalMouseMove);

            return () => {
                document.removeEventListener('mousemove', handleGlobalMouseMove);
            };
        }
    }, [isHoverDayOpen, forceClosePopup]);

    // Global mouse listener to close hover popup when moving away
    useEffect(() => {
        if (isHoverOpen) {
            const handleGlobalMouseMove = (e) => {
                // Check if mouse is outside both the specific event element and hover popup
                const eventElement = hoverEventAnchor.current;
                const hoverPopup = document.querySelector('.md-tooltip-container');

                if (eventElement && hoverPopup) {
                    const eventRect = eventElement.getBoundingClientRect();
                    const popupRect = hoverPopup.getBoundingClientRect();

                    const isOutsideEvent = !(
                        e.clientX >= eventRect.left &&
                        e.clientX <= eventRect.right &&
                        e.clientY >= eventRect.top &&
                        e.clientY <= eventRect.bottom
                    );

                    const isOutsidePopup = !(
                        e.clientX >= popupRect.left &&
                        e.clientX <= popupRect.right &&
                        e.clientY >= popupRect.top &&
                        e.clientY <= popupRect.bottom
                    );

                    if (isOutsideEvent && isOutsidePopup) {
                        forceCloseHoverPopup();
                    }
                }
            };

            document.addEventListener('mousemove', handleGlobalMouseMove);

            return () => {
                document.removeEventListener('mousemove', handleGlobalMouseMove);
            };
        }
    }, [isHoverOpen, forceCloseHoverPopup]);

    // Global mouse listener to close resource 11 popup when moving away
    useEffect(() => {
        if (isResource11PopupOpen) {
            const handleGlobalMouseMove = (e) => {
                if (resource11Anchor.current && resource11PopupRef.current) {
                    const anchorRect = resource11Anchor.current.getBoundingClientRect();
                    const popupRect = resource11PopupRef.current.getBoundingClientRect();

                    // Check if mouse is outside both the event element and popup
                    const isOutsideEvent = !(
                        e.clientX >= anchorRect.left &&
                        e.clientX <= anchorRect.right &&
                        e.clientY >= anchorRect.top &&
                        e.clientY <= anchorRect.bottom
                    );

                    const isOutsidePopup = !(
                        e.clientX >= popupRect.left &&
                        e.clientX <= popupRect.right &&
                        e.clientY >= popupRect.top &&
                        e.clientY <= popupRect.bottom
                    );

                    if (isOutsideEvent && isOutsidePopup) {
                        forceCloseResource11Popup();
                    }
                }
            };

            document.addEventListener('mousemove', handleGlobalMouseMove);

            return () => {
                document.removeEventListener('mousemove', handleGlobalMouseMove);
            };
        }
    }, [isResource11PopupOpen, forceCloseResource11Popup]);

    // Scroll handler to close hover popup and resource 11 popup
    useEffect(() => {
        const handleScroll = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            // Close hover popup
            setIsHoverOpen(false);
            // Clear the hover event anchor reference
            if (hoverEventAnchor.current) {
                hoverEventAnchor.current = null;
            }
            // Close resource 11 popup
            setIsResource11PopupOpen(false);
            setResource11Description(null);
        };

        // Listen to scroll events on various containers
        document.addEventListener('scroll', handleScroll, true);
        window.addEventListener('scroll', handleScroll, true);

        // Listen to scroll events on calendar-specific containers
        const calendarContainer = document.querySelector('.motion-air-calendar-wrapper');
        if (calendarContainer) {
            calendarContainer.addEventListener('scroll', handleScroll, true);
        }

        const timelineContainer = document.querySelector('.mbsc-timeline-grid-scroll');
        if (timelineContainer) {
            timelineContainer.addEventListener('scroll', handleScroll, true);
        }

        // Return cleanup function
        return () => {
            document.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('scroll', handleScroll, true);
            if (calendarContainer) {
                calendarContainer.removeEventListener('scroll', handleScroll, true);
            }
            if (timelineContainer) {
                timelineContainer.removeEventListener('scroll', handleScroll, true);
            }
        };
    }, []);

    // Escape key handler to close hover popup
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isHoverOpen) {
                forceCloseHoverPopup();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isHoverOpen, forceCloseHoverPopup]);

    // Page loaded handler — re-apply computed parent row heights after timeline render
    const onPageLoaded = useCallback(() => {
        if (!rowHeightsById?.size) return;
        applyTimelineRowHeights(rowHeightsById);
        applySummaryLineLanePositions();
    }, [rowHeightsById]);


    const myDayContent = createMyDayContent(norwegianHolidays, setIsHoverDayOpen, setHolidayName, setCurrentHolidayId, holidayAnchor);




    const myHeader = () => {
        return (
            <React.Fragment>
                <CalendarNav />
                <CalendarPrev />
                <CalendarToday />
                <CalendarNext />
                <div className="month-button-group" style={{ display: 'inline-flex', gap: '4px', marginLeft: 8 }}>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((label, monthIndex) => (
                        <Button
                            key={label}
                            size="small"
                            onClick={() => {
                                const year = mySelectedDate instanceof Date && !isNaN(mySelectedDate) ? mySelectedDate.getFullYear() : new Date().getFullYear();
                                const targetDate = new Date(year, monthIndex, 1);
                                if (timelineRef.current && timelineRef.current.navigate) {
                                    timelineRef.current.navigate(targetDate);
                                }
                                // Ensure selected date state is updated so year-change effects run
                                setSelectedDate(targetDate);
                            }}
                            sx={{ fontSize: '10px', minWidth: '36px', padding: '2px 6px' }}
                        >
                            {label}
                        </Button>
                    ))}
                </div>
                <Button
                    id={"btnHideResource"}
                    onClick={showMobileMenu}
                    sx={{ fontSize: "10px" }}
                >
                    {mobileShow ? "Hide resource" : "Show resource"}
                </Button>
                {/* ✅ LIVEBLOCKS: Re-enable user avatars */}
                <div className="cursor-users-online flex pl-3">
                    {(() => {
                        if (!REMOVE_DUPLICATE_AVATARS) {
                            // Show all avatars without deduplication (for debugging)
                            return others
                                .filter(({ presence }) => presence?.name && presence?.color)
                                .slice(0, 3)
                                .map(({ connectionId, presence }) => (
                                    <Avatar
                                        key={connectionId}
                                        connectionId={connectionId}
                                        color={presence.color}
                                        name={presence.name}
                                        displayName={presence.displayName || presence.name}
                                        lastActive={presence.lastActive}
                                        scrollToUser={scrollToUser}
                                        profilePicture={presence.profilePicture}
                                    />
                                ));
                        }

                        // Improved deduplication: Group by email and pick the most recent connection
                        // Exclude self from others to prevent duplicate avatars
                        const userMap = new Map();
                        const selfEmail = self?.presence?.name;
                        
                        others.forEach(({ connectionId, presence }) => {
                            if (!presence?.name || !presence?.color) return;
                            
                            // Skip if this is the current user (self)
                            if (presence.name === selfEmail) return;
                            
                            const email = presence.name;
                            const existing = userMap.get(email);
                            
                            // Keep the user with the most recent lastActive time, or the first one if no lastActive
                            if (!existing || 
                                (presence.lastActive && (!existing.presence.lastActive || 
                                 new Date(presence.lastActive) > new Date(existing.presence.lastActive)))) {
                                userMap.set(email, { connectionId, presence });
                            }
                        });
                        
                        const uniqueUsers = Array.from(userMap.values());
                        
                        
                        return uniqueUsers.slice(0, 3).map(({ connectionId, presence }) => (
                            <Avatar
                                key={connectionId}
                                connectionId={connectionId}
                                color={presence.color}
                                name={presence.name}
                                displayName={presence.displayName || presence.name}
                                lastActive={presence.lastActive}
                                scrollToUser={scrollToUser}
                                profilePicture={presence.profilePicture}
                            />
                        ));
                    })()}
                    {self?.presence && (
                        <Avatar
                            connectionId="self"
                            color={self.presence.color}
                            name={self.presence.name}
                            displayName={'You'}
                            scrollToUser={scrollToUser}
                            profilePicture={userSettings?.showProfilePictureInAvatar ? userSettings?.userImage : ""}
                        />
                    )}
                </div>
            </React.Fragment>
        );
    }

    // Simple goToToday function
    const goToToday = React.useCallback(() => {
        const today = new Date();
        if (timelineRef.current && timelineRef.current.navigate) {
            timelineRef.current.navigate(today);
        }
    }, []);

    const goToDate = React.useCallback((date) => {
        if (timelineRef.current && timelineRef.current.navigate) {
            timelineRef.current.navigate(date);
        }
    }, []);
    const contextValue = useMemo(() => {
        return { goToToday, goToDate };
    }, [goToToday, goToDate]);

    // Create Eventcalendar configuration object
    const eventCalendarConfig = createEventCalendarConfig({
        timelineRef,
        zoomLevel,
        mySelectedDate,
        myEvents: [...(filteredEvents || []), ...(summaryEvents || [])],
        resources: resourcesWithVacation,
        calendarColors,
        invalidResources,
        onSelectedDateChange,
        onPageLoaded,
        onEventClick,
        onEventCreated,
        onEventDelete,
        onEventDeleted,
        onEventUpdated,
        onEventHoverIn,
        onEventHoverOut,
        myScheduleEvent,
        // myScheduleEventContent,
        myHeader,
        myDayContent,
        myRenderResource
    });
    return <CalendarContext.Provider value={contextValue}>
        <div>
            {isLoading ? ( // Unified loading state (auth + events)
                <LoadingSpinner />
            ) : (
                <div
                    className='motion-air-calendar-wrapper'
                    style={{ position: 'relative' }}
                    onPointerMove={mouseMove}
                    onPointerLeave={updatePresence}
                >
                    {user ? <div>
                        {/* ✅ LIVEBLOCKS: Render deduplicated cursors (one per user/email) */}
                        {(() => {
                            // When disabled, show all cursors without deduplication (for debugging)
                            if (!REMOVE_DUPLICATE_AVATARS) {
                                return others.map(({ connectionId, presence }) => {
                                    const hasCursor = presence?.cursor && typeof presence.cursor.x === 'number' && typeof presence.cursor.y === 'number';
                                    const hasColor = presence?.color && presence.color.default && presence.color.dim;
                                    if (!hasCursor || !hasColor) return null;
                                    return (
                                        <Cursor
                                            key={connectionId}
                                            x={cursorPosCalcX(presence)}
                                            y={cursorPosCalcY(presence)}
                                            name={presence.displayName || presence.name || 'Unknown'}
                                            color={presence.color}
                                            profilePicture={presence.profilePicture}
                                            lastActive={presence.lastActive}
                                        />
                                    );
                                });
                            }

                            // Deduplicate cursors by user/email, prefer most recent connection
                            const userMap = new Map();
                            const selfEmail = self?.presence?.name;

                            others.forEach(({ connectionId, presence }) => {
                                const hasCursor = presence?.cursor && typeof presence.cursor.x === 'number' && typeof presence.cursor.y === 'number';
                                const hasColor = presence?.color && presence.color.default && presence.color.dim;
                                const email = presence?.name;

                                if (!hasCursor || !hasColor || !email) return;
                                if (email === selfEmail) return; // exclude self

                                const existing = userMap.get(email);
                                const candidate = { connectionId, presence };

                                if (!existing) {
                                    userMap.set(email, candidate);
                                    return;
                                }

                                const existingTime = existing.presence?.lastActive ? new Date(existing.presence.lastActive).getTime() : 0;
                                const candidateTime = presence?.lastActive ? new Date(presence.lastActive).getTime() : 0;

                                if (candidateTime >= existingTime) {
                                    userMap.set(email, candidate);
                                }
                            });

                            const uniqueUsers = Array.from(userMap.values());
                            return uniqueUsers.map(({ connectionId, presence }) => (
                                <Cursor
                                    key={connectionId}
                                    x={cursorPosCalcX(presence)}
                                    y={cursorPosCalcY(presence)}
                                    name={presence.displayName || presence.name || 'Unknown'}
                                    color={presence.color}
                                    profilePicture={presence.profilePicture}
                                    lastActive={presence.lastActive}
                                />
                            ));
                        })()}
                        <Eventcalendar {...eventCalendarConfig} />

                        <EventPopupManager
                            {...createEventPopupManagerConfig({
                                // Main event popup
                                isOpen,
                                setOpen,
                                isEdit,
                                isManualSaving,
                                saveEvent,
                                onClose,
                                headerText,
                                anchor,
                                onDelete: onDeleteClick,
                                onAvailabilityChange: handleAvailabilityChange,
                                onHandleEventXChange: handleEventX,
                                onHandleEventInvoicedChange: handleEventInvoiced,
                                popupEventTitle,
                                titleChange,
                                popupEventDescription,
                                descriptionChange,
                                selectedColor,
                                availability,
                                showAvailabilitySelect: !popupIsRentman,
                                isX,
                                isEditable,
                                popupEventAllDay,
                                allDayChange,
                                popupEventDate,
                                changeStartDate,
                                changeEndDate,
                                startRef,
                                endRef,
                                isChecked,
                                handleResourceCheckInEvent,
                                popupButtons,
                                responsivePopup,
                                changeColor,

                                // Hover popup
                                hoverIsBusy,
                                hoverIsVacation,
                                popupEventTime,
                                popupEventCreated,
                                popupEventEdited,

                                // Resources and utilities
                                ...createResourcesAndUtilsConfig({
                                    resources,
                                    flattenArr,
                                    getDateFromTimestamp,
                                    eventContent,
                                    isHoverOpen,
                                    onMouseEnter,
                                    onMouseLeave
                                })
                            })}
                        />

                        {/* Holiday Popup - Moved back to CalendarComponent where it was working */}
                        {holidayAnchor &&
                            holidayAnchor.current &&
                            isHoverDayOpen &&
                            holidayName && (
                                <div
                                    ref={holidayPopupRef}
                                    className="holiday-popup"
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        backgroundColor: 'white',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        padding: '8px 12px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        zIndex: 1000,
                                        pointerEvents: 'none',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '300px',
                                        opacity: 0, // Start invisible
                                        transition: 'opacity 0.1s ease-in' // Smooth fade in
                                    }}
                                    onMouseEnter={onHolidayMouseEnter}
                                    onMouseLeave={onHolidayMouseLeave}
                                >
                                    <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>{holidayName}</p>
                                </div>
                            )}

                        {/* Resource 11 Popup - Simple description popup for Rentman events */}
                        {resource11Anchor &&
                            resource11Anchor.current &&
                            isResource11PopupOpen &&
                            resource11Description && (
                                <div
                                    ref={resource11PopupRef}
                                    className="resource11-popup"
                                    style={{
                                        position: 'fixed',
                                        left: 0,
                                        top: 0,
                                        backgroundColor: 'white',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        padding: '8px 12px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        zIndex: 1000,
                                        pointerEvents: 'none',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '300px',
                                        opacity: 0, // Start invisible
                                        transition: 'opacity 0.1s ease-in' // Smooth fade in
                                    }}
                                    onMouseEnter={onResource11MouseEnter}
                                    onMouseLeave={onResource11MouseLeave}
                                >
                                    <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>{resource11Description}</p>
                                </div>
                            )}


                    </div> : <></>}
                </div>
            )}
        </div>
    </CalendarContext.Provider>
});

export default Calendar;
